// downloadMarkdown.ts
type Newline = "lf" | "crlf" | "cr";

function normalizeNewlines(s: string, style: Newline): string {
  const nl = style === "crlf" ? "\r\n" : style === "cr" ? "\r" : "\n";
  return s.replace(/\r\n|\r|\n/g, nl);
}

const stripEmojisAndMarkdown = (text: string) => {
  // Remove emojis
  let result = text.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF]|[\u2011-\u26FF]|[\uFE00-\uFE0F])/g, "");
  // Remove markdown formatting
  result = result.replace(/[*_#\[\]\(\)~`>+\-=|{}!]/g, "");
  return result;
}

export function downloadMarkdown(
  markdown: string,
  filename = "document.md",
  opts: { newline?: Newline } = {}
) {
  const text = normalizeNewlines(markdown, opts.newline ?? "lf");
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  filename = stripEmojisAndMarkdown(filename);
  a.download = filename.endsWith(".md") ? filename : `${filename}.md`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function saveMarkdown(
  markdown: string,
  citations: any[],
  filename = "document.md",
  opts: { newline?: Newline } = {}
) {
  const citationSection = citations.length
    ? `\n\n## Citations\n\n${citations.map(c => `[${c.id}] ${c.title}`).join("\n")}`
    : "";
  const both = markdown + citationSection;
  const text = normalizeNewlines(both, opts.newline ?? "lf");
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });

  // File System Access API (Chromium/Edge desktop, some Android)
  const w = window as unknown as {
    showSaveFilePicker?: (options: any) => Promise<any>;
  };

  if (w.showSaveFilePicker) {
    const handle = await w.showSaveFilePicker({
      suggestedName: filename,
      types: [
        {
          description: "Markdown",
          accept: { "text/markdown": [".md"], "text/plain": [".md"] },
        },
      ],
      excludeAcceptAllOption: false,
    });
    const stream = await handle.createWritable();
    await stream.write(blob);
    await stream.close();
  } else {
    // Fallback to anchor download
    downloadMarkdown(text, filename);
  }
}
